/**
 * Shared route annotation logic for LiveMap and RouteHistory.
 *
 * Provides:
 *  - direction arrows every N positions
 *  - STOP events (ignition=true, speed≈0, >60 s)
 *  - Parking events (ignition=false, >3 min)
 *  - Leaflet DivIcons for all three
 */

import L from 'leaflet';
import { Position } from '../types';
import { parseUtc } from './dataAge';

function parseAttrs(raw: string | null | undefined): Record<string, any> {
  if (!raw) return {};
  try { return typeof raw === 'object' ? (raw as any) : JSON.parse(raw); } catch { return {}; }
}

/** Format ISO timestamp to local HH:MM:SS using the browser's timezone. */
export function fmtLocalTime(iso: string): string {
  return parseUtc(iso).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

// ── Types ──────────────────────────────────────────────────────────────────

export interface RouteEvent {
  position:    [number, number];
  startTime:   string;   // ISO
  durationMin: number;
}

export interface ArrowData {
  position:  [number, number];
  speedKmh:  number;
  rpm:       number | null;
  fixtime:   string;
  icon:      L.DivIcon;  // pre-built; stable reference from useMemo
}

// ── Builders ──────────────────────────────────────────────────────────────

/**
 * Arrow marker every `step` positions (chronological order).
 * Bearing derived from the preceding point → current point.
 */
export function buildArrowData(positions: Position[], step = 5): ArrowData[] {
  const result: ArrowData[] = [];
  for (let i = step; i < positions.length; i += step) {
    const prev     = positions[i - 1];
    const curr     = positions[i];
    const dLng     = curr.longitude - prev.longitude;
    const dLat     = curr.latitude  - prev.latitude;
    const angleDeg = Math.atan2(dLng, dLat) * 180 / Math.PI + 180;
    const attrs    = parseAttrs(curr.attributes);
    const rpmRaw   = attrs.rpm ?? attrs.io85;
    result.push({
      position: [curr.latitude, curr.longitude],
      speedKmh: Math.round(curr.speed_kmh ?? 0),
      rpm:      rpmRaw != null ? Math.round(Number(rpmRaw)) : null,
      fixtime:  curr.fixtime,
      icon:     makeArrowIcon(angleDeg),
    });
  }
  return result;
}

/**
 * STOP events: ignition=true AND speed < 1 km/h for more than 120 seconds.
 * Positions must be in chronological order (oldest first).
 */
export function detectStopEvents(positions: Position[]): RouteEvent[] {
  const events: RouteEvent[] = [];
  let startIdx: number | null = null;

  for (let i = 0; i < positions.length; i++) {
    const ignition = parseAttrs(positions[i].attributes).ignition;
    const active =
      (ignition === true || ignition === 'true') &&
      (positions[i].speed_kmh ?? 0) < 1;

    if (active && startIdx === null) {
      startIdx = i;
    } else if (!active && startIdx !== null) {
      const dur =
        (parseUtc(positions[i].fixtime).getTime() -
         parseUtc(positions[startIdx].fixtime).getTime()) / 1000;
      if (dur > 120) {
        events.push({
          position:    [positions[startIdx].latitude, positions[startIdx].longitude],
          startTime:   positions[startIdx].fixtime,
          durationMin: Math.round(dur / 60),
        });
      }
      startIdx = null;
    }
  }

  // Still in a stop at the end of the track
  if (startIdx !== null && positions.length > 0) {
    const dur =
      (parseUtc(positions[positions.length - 1].fixtime).getTime() -
       parseUtc(positions[startIdx].fixtime).getTime()) / 1000;
    if (dur > 60) {
      events.push({
        position:    [positions[startIdx].latitude, positions[startIdx].longitude],
        startTime:   positions[startIdx].fixtime,
        durationMin: Math.round(dur / 60),
      });
    }
  }

  return events;
}

/**
 * Parking events: ignition=false for more than 3 minutes.
 * Positions must be in chronological order (oldest first).
 */
export function detectParkingEvents(positions: Position[]): RouteEvent[] {
  const events: RouteEvent[] = [];
  let startIdx: number | null = null;

  for (let i = 0; i < positions.length; i++) {
    const ignition = parseAttrs(positions[i].attributes).ignition;
    const parked = ignition === false || ignition === 'false';

    if (parked && startIdx === null) {
      startIdx = i;
    } else if (!parked && startIdx !== null) {
      const dur =
        (parseUtc(positions[i].fixtime).getTime() -
         parseUtc(positions[startIdx].fixtime).getTime()) / 1000;
      if (dur > 180) {
        events.push({
          position:    [positions[startIdx].latitude, positions[startIdx].longitude],
          startTime:   positions[startIdx].fixtime,
          durationMin: Math.round(dur / 60),
        });
      }
      startIdx = null;
    }
  }

  // Still parked at the end of the track
  if (startIdx !== null && positions.length > 0) {
    const dur =
      (parseUtc(positions[positions.length - 1].fixtime).getTime() -
       parseUtc(positions[startIdx].fixtime).getTime()) / 1000;
    if (dur > 180) {
      events.push({
        position:    [positions[startIdx].latitude, positions[startIdx].longitude],
        startTime:   positions[startIdx].fixtime,
        durationMin: Math.round(dur / 60),
      });
    }
  }

  return events;
}

// ── Leaflet icons ──────────────────────────────────────────────────────────

/** White SVG triangle with dark outline, rotated to bearing. */
export function makeArrowIcon(angleDeg: number): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"
        style="transform:rotate(${angleDeg}deg);display:block;overflow:visible">
      <polygon points="7,1 13,13 1,13"
        fill="white" stroke="#555" stroke-width="1.5" stroke-linejoin="round"/>
    </svg>`,
    iconSize:   [14, 14],
    iconAnchor: [7, 7],
  });
}

// Singletons — created once after Leaflet is loaded
let _stopIcon:    L.DivIcon | null = null;
let _parkingIcon: L.DivIcon | null = null;

/** Red octagon with "STOP" text (road sign B-20). */
export function makeStopIcon(): L.DivIcon {
  if (!_stopIcon) {
    _stopIcon = L.divIcon({
      className: '',
      html: `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg" style="display:block">
        <polygon points="10,2 18,2 26,10 26,18 18,26 10,26 2,18 2,10"
          fill="#dc2626" stroke="white" stroke-width="2" stroke-linejoin="round"/>
        <text x="14" y="17.5" text-anchor="middle" fill="white"
          font-size="6.5" font-weight="bold" font-family="Arial,sans-serif" letter-spacing="-0.3">STOP</text>
      </svg>`,
      iconSize:   [28, 28],
      iconAnchor: [14, 14],
    });
  }
  return _stopIcon;
}

/** Blue rectangle with "P" (road sign D-18 parking). */
export function makeParkingIcon(): L.DivIcon {
  if (!_parkingIcon) {
    _parkingIcon = L.divIcon({
      className: '',
      html: `<svg width="26" height="26" viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg" style="display:block">
        <rect x="1" y="1" width="24" height="24" rx="4"
          fill="#2b6cc4" stroke="white" stroke-width="2"/>
        <text x="13" y="19" text-anchor="middle" fill="white"
          font-size="15" font-weight="bold" font-family="Arial,sans-serif">P</text>
      </svg>`,
      iconSize:   [26, 26],
      iconAnchor: [13, 13],
    });
  }
  return _parkingIcon;
}
